export const STANDARD_TOURNAMENT_CATEGORIES = [
  { id: 'mens_singles', label: "Men's Singles", filterLabel: "MEN'S" },
  { id: 'womens_singles', label: "Women's Singles", filterLabel: "WOMEN'S" },
  { id: 'mens_doubles', label: "Men's Doubles", filterLabel: "MEN'S DOUBLES" },
  { id: 'womens_doubles', label: "Women's Doubles", filterLabel: "WOMEN'S DOUBLES" },
  { id: 'mixed_doubles', label: 'Mixed Doubles', filterLabel: 'MIXED DOUBLES' },
];

/** Legacy Men's/Women's Mixed Doubles IDs → unified Mixed Doubles. */
export const LEGACY_CATEGORY_ALIASES = {
  mens_mixed_doubles: 'mixed_doubles',
  womens_mixed_doubles: 'mixed_doubles',
};

export const normalizeCategoryId = (categoryId) => {
  const id = String(categoryId || '').trim();
  return LEGACY_CATEGORY_ALIASES[id] || id;
};

export const OPEN_SINGLES_CATEGORIES = [
  { id: 'open_mens_singles', label: "Open Men's Singles", filterLabel: "OPEN MEN'S" },
  { id: 'open_womens_singles', label: "Open Women's Singles", filterLabel: "OPEN WOMEN'S" },
];

export const TOURNAMENT_CATEGORIES = [
  ...STANDARD_TOURNAMENT_CATEGORIES,
  ...OPEN_SINGLES_CATEGORIES,
];

/** Tournament type filter pills — one per category for easy finding. */
export const CATEGORY_FILTER_PILLS = [
  { id: 'mens_singles', label: "MEN'S SINGLES" },
  { id: 'womens_singles', label: "WOMEN'S SINGLES" },
  { id: 'mens_doubles', label: "MEN'S DOUBLES" },
  { id: 'womens_doubles', label: "WOMEN'S DOUBLES" },
  { id: 'mixed_doubles', label: 'MIXED DOUBLES' },
  { id: 'open_mens_singles', label: "OPEN MEN'S SINGLES" },
  { id: 'open_womens_singles', label: "OPEN WOMEN'S SINGLES" },
];

export const SKILL_LEVELS = ['Novice', 'Intermediate', 'Advanced', 'Open Single'];

/** Normalize legacy "Beginner" → "Novice" for display and forms. */
export const normalizeSkillLevel = (value, fallback = 'Novice') => {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  if (raw.toLowerCase() === 'beginner') return 'Novice';
  const match = SKILL_LEVELS.find((level) => level.toLowerCase() === raw.toLowerCase());
  return match || raw;
};

export const isOpenSinglesCategory = (categoryId) =>
  String(categoryId || '').startsWith('open_');

export const isOpenSingleSkill = (skill) =>
  normalizeSkillLevel(skill, '') === 'Open Single';

/** Categories allowed for a skill: Open Single → open singles only; else standard 5. */
export const categoriesForSkill = (skill) =>
  (isOpenSingleSkill(skill) ? OPEN_SINGLES_CATEGORIES : STANDARD_TOURNAMENT_CATEGORIES);

export const categoryAllowedForSkill = (categoryId, skill) => {
  const normalized = normalizeCategoryId(categoryId);
  return categoriesForSkill(skill).some((c) => c.id === normalized);
};

/**
 * Category chips/filters for browse UIs.
 * skillFilter === 'all' → every category; Open Single → open pair only; else standard 5 (no open).
 */
export const categoriesForSkillFilter = (skillFilter) => {
  if (!skillFilter || skillFilter === 'all') return TOURNAMENT_CATEGORIES;
  return categoriesForSkill(skillFilter);
};

export const categoryFilterPillsForSkill = (skillFilter) => {
  const allowed = new Set(categoriesForSkillFilter(skillFilter).map((c) => c.id));
  return CATEGORY_FILTER_PILLS.filter((pill) => allowed.has(pill.id));
};

export const SKILL_RANK = { Novice: 0, Intermediate: 1, Advanced: 2, 'Open Single': 3 };

export const skillRank = (value) => SKILL_RANK[normalizeSkillLevel(value)] ?? 0;

/** DUPR → skill bands: Novice ≤2.99, Intermediate 3.00–3.99, Advanced ≥4.00. */
export const DUPR_SKILL_BANDS = {
  Novice: { max: 2.99 },
  Intermediate: { min: 3.0, max: 3.99 },
  Advanced: { min: 4.0 },
};

/** Parse a DUPR rating string/number → finite number or null. */
export const parseDuprRating = (raw) => {
  if (raw == null || raw === '') return null;
  const cleaned = String(raw).trim().replace(/,/g, '');
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0 || n > 8) return null;
  return Math.round(n * 100) / 100;
};

/** Map DUPR rating → Novice | Intermediate | Advanced (never Open Single). */
export const skillFromDupr = (ratingOrRaw) => {
  const rating = typeof ratingOrRaw === 'number' ? ratingOrRaw : parseDuprRating(ratingOrRaw);
  if (rating == null) return null;
  if (rating <= 2.99) return 'Novice';
  if (rating <= 3.99) return 'Intermediate';
  return 'Advanced';
};

/**
 * Whether a DUPR rating is allowed for a division skill.
 * Open Single: no DUPR limit. Missing/unparseable rating fails for N/I/A.
 */
export const duprMatchesSkill = (ratingOrRaw, skill) => {
  const division = normalizeSkillLevel(skill, '');
  if (!division) return false;
  if (isOpenSingleSkill(division)) return true;
  const derived = skillFromDupr(ratingOrRaw);
  return derived === division;
};

/**
 * Public registration: exact skill match + DUPR band match (Open Single skips DUPR band).
 */
export const skillEligibility = (userSkill, tournamentSkill, duprRating = null) => {
  const player = normalizeSkillLevel(userSkill);
  const division = normalizeSkillLevel(tournamentSkill);
  if (player !== division) {
    return {
      status: 'mismatch',
      allowed: false,
      player,
      division,
      duprOk: true,
      derivedSkill: skillFromDupr(duprRating),
      reason: 'skill',
    };
  }
  if (!duprMatchesSkill(duprRating, division)) {
    const derived = skillFromDupr(duprRating);
    return {
      status: 'mismatch',
      allowed: false,
      player,
      division,
      duprOk: false,
      derivedSkill: derived,
      reason: 'dupr',
    };
  }
  return {
    status: 'match',
    allowed: true,
    player,
    division,
    duprOk: true,
    derivedSkill: skillFromDupr(duprRating),
    reason: null,
  };
};

/** Short copy for eligibility failure (toast / wizard). */
export const skillEligibilityMessage = (eligibility, tournamentSkill) => {
  const division = normalizeSkillLevel(tournamentSkill || eligibility?.division);
  if (!eligibility || eligibility.allowed) return '';
  if (eligibility.reason === 'dupr') {
    const rating = eligibility.derivedSkill
      ? `Your DUPR maps to ${eligibility.derivedSkill}`
      : 'Your DUPR rating could not be read';
    return `${rating} — this is a ${division} event. Update your DUPR or join a matching division.`;
  }
  return `This ${division} event is only open to ${division} players (your level: ${eligibility.player}).`;
};

/**
 * Distinct visual identity per skill so players can tell divisions apart at a glance.
 * Used by TournamentBanner + Tournament registration panel filters/badges.
 */
export const SKILL_LEVEL_THEMES = {
  Novice: {
    id: 'Novice',
    label: 'Novice',
    short: 'NOV',
    hint: 'New & developing players',
    description: 'New & developing players — friendly rallies, learning the rules, and first-time tournament nerves welcome.',
    // Clear green — approachable / entry division
    fg: '#15803d',
    bg: '#dcfce7',
    border: '#86efac',
    solid: '#16a34a',
    onSolid: '#ffffff',
    chipIdle: 'bg-green-50 text-green-800 ring-green-200',
    chipActive: 'bg-green-600 text-white ring-green-600 shadow-md shadow-green-600/30',
    badge: 'bg-green-500 text-white',
    badgeSoft: 'bg-green-100 text-green-900 ring-1 ring-green-300',
    cardAccent: '#22c55e',
  },
  Intermediate: {
    id: 'Intermediate',
    label: 'Intermediate',
    short: 'INT',
    hint: 'Competitive club level',
    description: 'Competitive club level — consistent serves and returns, dinking battles, and steady court coverage.',
    // Warm amber — standout mid tier
    fg: '#b45309',
    bg: '#fef3c7',
    border: '#fcd34d',
    solid: '#d97706',
    onSolid: '#ffffff',
    chipIdle: 'bg-amber-50 text-amber-900 ring-amber-200',
    chipActive: 'bg-amber-500 text-white ring-amber-500 shadow-md shadow-amber-500/35',
    badge: 'bg-amber-500 text-white',
    badgeSoft: 'bg-amber-100 text-amber-950 ring-1 ring-amber-300',
    cardAccent: '#f59e0b',
  },
  Advanced: {
    id: 'Advanced',
    label: 'Advanced',
    short: 'ADV',
    hint: 'High-level tournament play',
    description: 'High-level tournament play — fast hands, strategic stacking, and experienced medal chasers.',
    // Deep rose — elite signal
    fg: '#9f1239',
    bg: '#ffe4e6',
    border: '#fb7185',
    solid: '#e11d48',
    onSolid: '#ffffff',
    chipIdle: 'bg-rose-50 text-rose-900 ring-rose-200',
    chipActive: 'bg-rose-600 text-white ring-rose-600 shadow-md shadow-rose-600/35',
    badge: 'bg-rose-600 text-white',
    badgeSoft: 'bg-rose-100 text-rose-950 ring-1 ring-rose-300',
    cardAccent: '#f43f5e',
  },
  'Open Single': {
    id: 'Open Single',
    label: 'Open Single',
    short: 'OPEN',
    hint: 'Open singles division',
    description: "Open singles — exact Open Single skill required; Men's and Women's open singles only.",
    fg: '#1e3a8a',
    bg: '#dbeafe',
    border: '#93c5fd',
    solid: '#1e4b85',
    onSolid: '#ffffff',
    chipIdle: 'bg-sky-50 text-sky-900 ring-sky-200',
    chipActive: 'bg-[#1e4b85] text-white ring-[#1e4b85] shadow-md shadow-[#1e4b85]/35',
    badge: 'bg-[#1e4b85] text-white',
    badgeSoft: 'bg-sky-100 text-sky-950 ring-1 ring-sky-300',
    cardAccent: '#3b82f6',
  },
};

export const getSkillLevelTheme = (value) => {
  const skill = normalizeSkillLevel(value, '');
  return SKILL_LEVEL_THEMES[skill] || SKILL_LEVEL_THEMES.Novice;
};

/** True when tournament skill matches a filter (null/All = any). */
export const matchesSkillFilter = (tournament, skillFilter) => {
  if (!skillFilter || skillFilter === 'all') return true;
  return normalizeSkillLevel(tournament?.skill_level) === normalizeSkillLevel(skillFilter);
};

/** Banner / marketing title: "Novice Men's Singles" */
export const bannerTournamentTitle = (tournament) => {
  const skill = normalizeSkillLevel(tournament?.skill_level, '');
  const category = String(
    tournament?.category_label
    || tournament?.title
    || 'Tournament',
  ).trim();
  if (skill && category) return `${skill} ${category}`;
  return category || skill || 'Tournament';
};
export const AGE_GROUPS = ['18-25', '26-35', '36-45', '46-55', '18-50', '10-70', '18-70'];
/** Age bands that already include players 46+ / 50+ (no extra age-group option). */
export const SENIOR_FRIENDLY_AGE_GROUPS = ['46-55', '18-50', '10-70', '18-70'];

/** Parse "18-70" / "18–70" into min/max years, or null. */
export const parseAgeGroupRange = (ageGroup) => {
  const m = String(ageGroup || '').match(/(\d+)\s*[-–]\s*(\d+)/);
  if (!m) return null;
  const min = Number(m[1]);
  const max = Number(m[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min, max };
};

export const isSeniorFriendlyAgeGroup = (ageGroup) => {
  const text = String(ageGroup || '').trim();
  if (SENIOR_FRIENDLY_AGE_GROUPS.includes(text)) return true;
  const range = parseAgeGroupRange(text);
  return !!(range && range.max >= 46);
};

/** true / false when both age and band parse; otherwise null. */
export const ageFitsAgeGroup = (age, ageGroup) => {
  const range = parseAgeGroupRange(ageGroup);
  const n = Number(age);
  if (!range || !Number.isFinite(n)) return null;
  return n >= range.min && n <= range.max;
};

/** Human-readable age band, e.g. "Ages 18–70". */
export const formatAgeGroupLabel = (ageGroup) => {
  const raw = String(ageGroup || '').trim();
  if (!raw) return '';
  return `Ages ${raw.replace(/-/g, '–')}`;
};

/**
 * Learn More destination: signed-in players go to Events; guests stay on the public listing.
 */
export const tournamentLearnMorePath = (tournamentId, { isLoggedIn } = {}) => {
  const id = String(tournamentId || '').trim();
  if (isLoggedIn) {
    return id ? `/events?highlight=${encodeURIComponent(id)}` : '/events';
  }
  return id ? `/tournaments?highlight=${encodeURIComponent(id)}` : '/tournaments';
};

export const TOURNAMENT_STATUSES = ['upcoming', 'active', 'completed', 'cancelled'];

/**
 * Lifecycle status (organizer-controlled):
 * - upcoming  → before / until event day; registration can be open or closed
 * - active    → event day / in progress (Ongoing Tournament)
 * - completed → organizer ended the tournament (Tournament Ended)
 * - cancelled → called off
 *
 * Registration availability is separate: registration_closed flag OR past registration_deadline.
 */

/** Local calendar YYYY-MM-DD (avoids UTC off-by-one on date-only fields). */
export const todayLocalISODate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** True when registration_deadline date is before today. */
export const isRegistrationDeadlinePassed = (tournament) => {
  const deadline = String(tournament?.registration_deadline || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(deadline)) return false;
  return todayLocalISODate() > deadline;
};

/** True on or after the tournament calendar date. */
export const isTournamentDateReached = (tournament) => {
  const date = String(tournament?.tournament_date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  return todayLocalISODate() >= date;
};

/**
 * Organizer walk-ins stay available after public registration closes,
 * until the tournament date (and while the event is not ended).
 */
export const canOrganizerAddPlayers = (tournament) => {
  if (!tournament) return false;
  const status = String(tournament.status || '').toLowerCase();
  if (['completed', 'cancelled'].includes(status)) return false;
  if (isTournamentDateReached(tournament)) return false;
  return true;
};

/**
 * Effective registration closed = organizer closed flag, past deadline,
 * full capacity (0 slots left), or tournament already ended/cancelled.
 */
export const isRegistrationEffectivelyClosed = (tournament) => {
  if (!tournament) return true;
  const status = String(tournament.status || '').toLowerCase();
  if (['completed', 'cancelled'].includes(status)) return true;
  if (tournament.registration_closed) return true;
  if (isRegistrationDeadlinePassed(tournament)) return true;
  if (getRemainingSlots(tournament) === 0) return true;
  return false;
};

/** Homepage / listing status badge for a tournament. */
export const getTournamentBannerStatus = (tournament) => {
  if (!tournament) {
    return { id: 'upcoming', label: 'Upcoming', emoji: '⚪', tone: 'slate' };
  }
  const status = String(tournament.status || '').toLowerCase();

  if (status === 'cancelled') {
    return { id: 'cancelled', label: 'Cancelled', emoji: '⛔', tone: 'rose' };
  }
  if (status === 'completed') {
    return { id: 'ended', label: 'Tournament Ended', emoji: '🏁', tone: 'slate' };
  }
  if (status === 'active') {
    // Event in progress — registration usually closed, but still show closed if applicable
    if (isRegistrationEffectivelyClosed(tournament)) {
      return { id: 'ongoing', label: 'Ongoing · Reg. Closed', emoji: '🔵', tone: 'sky' };
    }
    return { id: 'ongoing', label: 'Ongoing Tournament', emoji: '🔵', tone: 'sky' };
  }

  // upcoming / open / default — badge follows registration window + deadline
  if (isRegistrationEffectivelyClosed(tournament)) {
    return { id: 'closed', label: 'Registration Closed', emoji: '🔴', tone: 'rose' };
  }
  if (status === 'upcoming' || status === 'open' || !status) {
    return { id: 'open', label: 'Registration Open', emoji: '🟢', tone: 'emerald' };
  }
  return { id: 'upcoming', label: 'Upcoming', emoji: '⚪', tone: 'slate' };
};

export const isRegistrationOpen = (tournament) => {
  if (!tournament) return false;
  if (isRegistrationEffectivelyClosed(tournament)) return false;
  const status = String(tournament.status || '').toLowerCase();
  return status === 'upcoming' || status === 'active' || status === 'open' || !status;
};

/** Organizer-set registration capacity (players for singles, pairs for doubles). */
export const MIN_PARTICIPANTS = 2;
export const MAX_PARTICIPANTS = 16;
/** Default field size: doubles/mixed = 8 pairs; singles = 16 players. */
export const DEFAULT_DOUBLES_SLOTS = 8;
export const DEFAULT_SINGLES_SLOTS = 16;

export const clampParticipantLimit = (value, fallback = MAX_PARTICIPANTS) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(MAX_PARTICIPANTS, Math.max(MIN_PARTICIPANTS, Math.round(n)));
};

/** Knockout stays at 8; 16-player fields are round robin (two pools of 8, 4 courts each). */
export const KNOCKOUT_FIELD_SIZES = [8];
export const RR_WINS_TO_QUALIFY = 4;
export const RR_LOSSES_TO_DQ = 4;
/** Games each entry may need before 4 wins or 4 losses (4+3). */
export const RR_GAMES_TO_FINISH = RR_WINS_TO_QUALIFY + RR_LOSSES_TO_DQ - 1;
/** Qualifying knockout after round robin: grows with 4-win qualifiers (2–16), not a fixed 4. */
export const RR_KO_SLOT_MAX = MAX_PARTICIPANTS;
/** @deprecated Use rrKnockoutSlotCount; kept as the old 4-seed default in copy fallbacks. */
export const RR_KO_SLOT_COUNT = 4;

/** How many KO seeds to show: 4-win qualifiers (and anyone already seated), capped by the RR field. */
export const rrKnockoutSlotCount = ({ qualifiedCount = 0, seatedCount = 0, fieldSize = 0 } = {}) => {
  const field = Math.max(0, Math.min(MAX_PARTICIPANTS, Number(fieldSize) || 0));
  const n = Math.max(0, Number(qualifiedCount) || 0, Number(seatedCount) || 0);
  if (field > 0) return Math.min(field, n);
  return Math.min(MAX_PARTICIPANTS, n);
};

/** Podium labels for the qualifying knockout (no 3rd when only two remain). */
export const rrKnockoutPlaceLabels = (slotCount) => {
  const n = Math.max(0, Number(slotCount) || 0);
  if (n <= 0) return [];
  if (n === 1) return ['1st'];
  if (n === 2) return ['1st', '2nd'];
  return ['1st', '2nd', '3rd'];
};

/** Short pairing hint on a seed tile (bye when the opponent is past the field). */
export const rrKnockoutSeedHint = (seed, slotCount) => {
  const n = Math.max(0, Number(slotCount) || 0);
  if (n < 2 || seed < 1) return '';
  const pair = openingPairings(n).find(([a, b]) => a === seed || b === seed);
  if (!pair) return '';
  const other = pair[0] === seed ? pair[1] : pair[0];
  if (other > n) return 'bye';
  return `plays seed ${other}`;
};

export const rrKnockoutSeedHeading = (slotCount) => {
  const n = Math.max(0, Number(slotCount) || 0);
  if (n <= 0) return 'Knockout seeds';
  if (n <= 2) return 'Knockout seeds (final 1v2)';
  const tree = n <= 4 ? 4 : n <= 8 ? 8 : 16;
  const byes = tree - n;
  const byeNote = byes > 0 ? `, ${byes} bye${byes === 1 ? '' : 's'}` : '';
  if (tree <= 4) return `Knockout seeds (${n} on a 4-draw${byeNote})`;
  if (tree <= 8) return `Knockout seeds (${n} on an 8-draw${byeNote})`;
  return `Knockout seeds (${n} on a 16-draw${byeNote})`;
};
export const RR_BYE_ID = '__bye__';
export const RR_COURT_COUNT = 4;
export const RR_GAME_MINUTES = 15;
export const RR_START_MINUTES = 8 * 60;
/** An 8-player pool fills all 4 courts in one round (smaller pools do not pad). */
export const RR_POSTER_POOL_SIZE = 8;

export const usesRoundRobin = (slotCount) => {
  const n = clampParticipantLimit(slotCount || 0);
  return n !== 8;
};

/** One pool under 10; two pools at 10–16 (5+5, 6+6, 8+8). */
export const roundRobinPoolCount = (slotCount) => {
  const n = clampParticipantLimit(slotCount || 0);
  if (!usesRoundRobin(n)) return 0;
  return n >= 10 ? 2 : 1;
};

export const roundRobinPoolSizes = (slotCount) => {
  const n = clampParticipantLimit(slotCount || 0);
  const pools = roundRobinPoolCount(n);
  if (pools <= 0) return [];
  if (pools === 1) return [n];
  const a = Math.floor(n / 2);
  return [a, n - a];
};

export const roundRobinPoolLabel = (index) => (index === 0 ? 'A' : 'B');

/** Slot caption: Player 1… continuously (Team 1… for doubles). */
export const roundRobinSlotCaption = (slotIndex, slotCount, { doubles = false } = {}) => {
  const sizes = roundRobinPoolSizes(slotCount);
  if (!sizes.length) return null;
  const n = slotIndex + 1;
  return doubles ? `Team ${n}` : `Player ${n}`;
};

export const roundRobinFormatTldr = (slotCount, { doubles = false } = {}) => {
  const n = clampParticipantLimit(slotCount || 0);
  if (!usesRoundRobin(n)) return null;
  const unit = doubles ? 'pairs' : 'players';
  const pools = roundRobinPoolCount(n);
  const poolNote = pools === 2 ? ' in two pools (A / B)' : ' in one pool';
  const places = rrKnockoutPlaceLabels(n).join(' / ') || '1st / 2nd / 3rd';
  return {
    title: `${n} ${unit}: round robin${poolNote} → qualifying knockout (${places})`,
    body: `Fields of 8 stay knockout. This ${n}-slot field is round robin${poolNote} — Player 1, Player 2, … cycle on 4 courts until ${RR_WINS_TO_QUALIFY} wins or ${RR_LOSSES_TO_DQ} losses. Click a name to award a match. Drag names to change pairings. Players who reach ${RR_WINS_TO_QUALIFY} wins wait in a pool; ${RR_LOSSES_TO_DQ} losses is DQ. Seat those qualifiers on a knockout that sizes itself (${places}) — 2 play a final, 3–4 use semifinals, 5–8 use a 8-draw with byes, 9+ a 16-draw with byes.`,
  };
};

/** Live Bracket dialog widths — round robin needs more room than a single elimination tree. */
export const PUBLIC_BRACKET_DIALOG_WIDTH = {
  elimination: 'w-[min(96vw,1100px)] max-w-[1100px]',
  roundRobin: 'w-[min(98vw,1280px)] max-w-[1280px]',
  roundRobinTwoPool: 'w-[min(98vw,1520px)] max-w-[1520px]',
  expanded: 'w-[min(99vw,1920px)] max-w-[1920px]',
};

/**
 * Pick a dialog width tier from the field size / format so the public bracket
 * is not squeezed (especially two-pool round robin: two schedules + standings).
 */
export const publicBracketDialogLayout = (tournament, { slotCount } = {}) => {
  const n = clampParticipantLimit(
    slotCount ?? tournament?.max_slots ?? tournament?.teams?.length ?? 0,
  );
  if (!usesRoundRobin(n)) {
    return {
      tier: 'elimination',
      widthClass: PUBLIC_BRACKET_DIALOG_WIDTH.elimination,
      expandedWidthClass: PUBLIC_BRACKET_DIALOG_WIDTH.expanded,
    };
  }
  const pools = roundRobinPoolCount(n);
  if (pools === 2) {
    return {
      tier: 'roundRobinTwoPool',
      widthClass: PUBLIC_BRACKET_DIALOG_WIDTH.roundRobinTwoPool,
      expandedWidthClass: PUBLIC_BRACKET_DIALOG_WIDTH.expanded,
    };
  }
  return {
    tier: 'roundRobin',
    widthClass: PUBLIC_BRACKET_DIALOG_WIDTH.roundRobin,
    expandedWidthClass: PUBLIC_BRACKET_DIALOG_WIDTH.expanded,
  };
};

export const defaultSlotsForCategory = (categoryId) =>
  (isDoublesCategory(categoryId) ? DEFAULT_DOUBLES_SLOTS : DEFAULT_SINGLES_SLOTS);

/** Statuses that count toward public registered_count (aligned with backend). */
export const PUBLIC_REGISTERED_STATUSES = new Set(['pending', 'approved', 'withdrawal_requested']);

/**
 * Strip private participant/bracket payloads from a WebSocket tournament update
 * so landing pages can merge instantly without exposing PII.
 */
export const publicTournamentFromRealtime = (raw) => {
  if (!raw?.tournament_id) return null;

  const participants = Array.isArray(raw.participants) ? raw.participants : null;
  let registeredCount = Number(raw.registered_count);
  let registeredCountOpen = Number(raw.registered_count_open);
  let registeredCount50plus = Number(raw.registered_count_50plus);
  if (participants) {
    const active = participants.filter(
      (p) => PUBLIC_REGISTERED_STATUSES.has(String(p?.status || '').toLowerCase()),
    );
    registeredCount = active.length;
    registeredCountOpen = active.filter(
      (p) => participantBracketDivision(p) === BRACKET_DIVISION_OPEN,
    ).length;
    registeredCount50plus = active.filter(
      (p) => participantBracketDivision(p) === BRACKET_DIVISION_50PLUS,
    ).length;
  } else if (!Number.isFinite(registeredCount)) {
    registeredCount = Number(raw.current_participants) || 0;
  }

  const img = raw.image_url || raw.image || null;
  const upcoming = raw.upcoming_image_url || raw.upcoming_image || null;

  return {
    tournament_id: raw.tournament_id,
    tournament_code: raw.tournament_code,
    title: raw.title,
    category: raw.category,
    category_label: raw.category_label,
    skill_level: normalizeSkillLevel(raw.skill_level),
    age_group: raw.age_group,
    tournament_date: raw.tournament_date,
    start_time: raw.start_time,
    end_time: raw.end_time,
    entry_fee: raw.entry_fee,
    entry_fee_per_player: !!raw.entry_fee_per_player,
    max_slots: raw.max_slots,
    current_participants: raw.current_participants || 0,
    registered_count: registeredCount,
    ...(Number.isFinite(registeredCountOpen) ? { registered_count_open: registeredCountOpen } : {}),
    ...(Number.isFinite(registeredCount50plus)
      ? { registered_count_50plus: registeredCount50plus }
      : {}),
    registration_deadline: raw.registration_deadline,
    registration_closed: !!raw.registration_closed,
    description: raw.description || '',
    tournament_type: raw.tournament_type,
    tournament_type_label: raw.tournament_type_label,
    status: raw.status,
    venue_id: raw.venue_id || 'venue_camsur',
    venue_name: raw.venue_name || DEFAULT_TOURNAMENT_VENUE,
    venue_address: raw.venue_address || DEFAULT_TOURNAMENT_VENUE_ADDRESS,
    image: img,
    image_url: img,
    image_updated_at: raw.image_updated_at,
    image_storage: raw.image_storage,
    upcoming_image: upcoming,
    upcoming_image_url: upcoming,
    upcoming_image_updated_at: raw.upcoming_image_updated_at,
    upcoming_image_storage: raw.upcoming_image_storage,
    // Prefer server flag; keep previous on merge if absent
    ...(typeof raw.bracket_ready === 'boolean' ? { bracket_ready: raw.bracket_ready } : {}),
    ...(typeof raw.bracket_ready_50plus === 'boolean'
      ? { bracket_ready_50plus: raw.bracket_ready_50plus }
      : {}),
    ...(raw.bracket_results && typeof raw.bracket_results === 'object'
      ? { bracket_results: raw.bracket_results }
      : {}),
    ...(raw.bracket_results_50plus && typeof raw.bracket_results_50plus === 'object'
      ? { bracket_results_50plus: raw.bracket_results_50plus }
      : {}),
    ...(raw.round_robin && typeof raw.round_robin === 'object'
      ? { round_robin: raw.round_robin }
      : {}),
    ...(raw.round_robin_50plus && typeof raw.round_robin_50plus === 'object'
      ? { round_robin_50plus: raw.round_robin_50plus }
      : {}),
    ...(Array.isArray(raw.teams) ? { teams: raw.teams } : {}),
    ...(Array.isArray(raw.teams_50plus) ? { teams_50plus: raw.teams_50plus } : {}),
    ...(raw.max_slots_50plus != null ? { max_slots_50plus: raw.max_slots_50plus } : {}),
  };
};

/** Merge a public-shaped tournament into a list (create/update; drop ended/cancelled). */
export const mergePublicTournamentList = (prev, incoming) => {
  const next = publicTournamentFromRealtime(incoming);
  if (!next?.tournament_id) return prev || [];
  const status = String(next.status || '').toLowerCase();
  const list = Array.isArray(prev) ? prev : [];
  if (['completed', 'cancelled'].includes(status)) {
    return list.filter((t) => t.tournament_id !== next.tournament_id);
  }
  const idx = list.findIndex((t) => t.tournament_id === next.tournament_id);
  if (idx === -1) return [next, ...list];
  const merged = { ...list[idx], ...next };
  // Don't wipe bracket_ready when WS payload omitted it
  if (typeof next.bracket_ready !== 'boolean' && typeof list[idx].bracket_ready === 'boolean') {
    merged.bracket_ready = list[idx].bracket_ready;
  }
  const out = [...list];
  out[idx] = merged;
  return out;
};

export const getRemainingSlots = (tournament) => {
  const max = clampParticipantLimit(tournament?.max_slots || MAX_PARTICIPANTS);
  const used = Number(tournament?.registered_count ?? tournament?.current_participants ?? 0);
  return Math.max(0, max - (Number.isFinite(used) ? used : 0));
};

/**
 * Under 50 vs 50+ remaining slots when the event runs two age brackets.
 * Each cohort uses the same max_slots field size.
 */
export const getAgeSplitSlotSummary = (tournament) => {
  if (!isAgeSplitCategory(tournament?.category)) return null;
  const max = clampParticipantLimit(tournament?.max_slots || MAX_PARTICIPANTS);
  const max50 = clampParticipantLimit(tournament?.max_slots_50plus ?? tournament?.max_slots ?? MAX_PARTICIPANTS);
  const openUsedRaw = Number(tournament?.registered_count_open);
  const seniorsUsedRaw = Number(tournament?.registered_count_50plus);
  const under50Used = Number.isFinite(openUsedRaw) ? Math.max(0, openUsedRaw) : 0;
  const seniorsUsed = Number.isFinite(seniorsUsedRaw) ? Math.max(0, seniorsUsedRaw) : 0;
  return {
    max,
    max50,
    under50Used,
    seniorsUsed,
    under50Left: Math.max(0, max - under50Used),
    seniorsLeft: Math.max(0, max50 - seniorsUsed),
  };
};

export const shortTournamentDescription = (tournament) => {
  const raw = String(tournament?.description || '').trim();
  if (raw) return raw.length > 140 ? `${raw.slice(0, 137)}…` : raw;
  const cat = tournament?.category_label || 'Pickleball';
  const skill = normalizeSkillLevel(tournament?.skill_level, 'All levels');
  return `${cat} · ${skill}. Join the Camsur Pickleball Club tournament and compete with the community.`;
};

/** Standard 16-player single-elim R1 pairings (seed a vs seed b). */
export const SINGLES_R1_PAIRINGS = [
  [1, 16], [8, 9], [4, 13], [5, 12],
  [2, 15], [7, 10], [3, 14], [6, 11],
];

/** 8-player single-elim opening round (quarterfinals). */
export const SINGLES_QF_PAIRINGS = [
  [1, 8], [4, 5], [2, 7], [3, 6],
];

/** 4-draw opening round (semifinals). Unused seeds are BYEs. */
export const SINGLES_SF_PAIRINGS = [
  [1, 4], [2, 3],
];

/** 2-draw: championship only. */
export const SINGLES_FINAL_PAIRINGS = [
  [1, 2],
];

/** Knockout tree size: next power of 2 (2, 4, 8, or 16). */
export const bracketTreeSize = (slotCount) => {
  const n = clampParticipantLimit(slotCount || MAX_PARTICIPANTS);
  if (n <= 2) return 2;
  if (n <= 4) return 4;
  if (n <= 8) return 8;
  return 16;
};

/** BYE count when the field is smaller than the power-of-2 tree. */
export const bracketByeCount = (slotCount) => {
  const n = clampParticipantLimit(slotCount || MAX_PARTICIPANTS);
  return Math.max(0, bracketTreeSize(n) - n);
};

/** Opening-round pairings for the field (BYEs fill seeds above max_slots). */
export const openingPairings = (slotCount) => {
  const tree = bracketTreeSize(slotCount);
  if (tree <= 2) return SINGLES_FINAL_PAIRINGS;
  if (tree <= 4) return SINGLES_SF_PAIRINGS;
  if (tree <= 8) return SINGLES_QF_PAIRINGS;
  return SINGLES_R1_PAIRINGS;
};

/** Both seeds are in the field — a match that must actually be played. */
export const isPlayInPairing = (pairing, fieldSize) => {
  const [a, b] = pairing || [];
  const n = Number(fieldSize) || 0;
  return Number(a) > 0 && Number(b) > 0 && Number(a) <= n && Number(b) <= n;
};

/** One seed is past max_slots — that match is a BYE (hidden on the tree). */
export const isByePairing = (pairing, fieldSize) => {
  const [a, b] = pairing || [];
  const n = Number(fieldSize) || 0;
  if (!(Number(a) > 0 && Number(b) > 0)) return false;
  return Number(a) > n || Number(b) > n;
};

/** Opening pairings that are real matches (not player-vs-BYE). */
export const playInPairings = (slotCount) => {
  const n = clampParticipantLimit(slotCount || MAX_PARTICIPANTS);
  return openingPairings(n).filter((p) => isPlayInPairing(p, n));
};

/**
 * Short why-the-draw-looks-like-this copy for non-power-of-2 fields
 * (e.g. 10 slots → 16-draw, 6 byes; play-ins 8 vs 9 and 7 vs 10).
 * Same math for singles, doubles, mixed, and open singles.
 */
export const byeBracketTldr = (slotCount, { doubles = false } = {}) => {
  const n = clampParticipantLimit(slotCount || 0);
  const byes = bracketByeCount(n);
  if (byes <= 0) return null;
  const tree = bracketTreeSize(n);
  const playIns = playInPairings(n);
  const unit = doubles ? 'pairs' : 'players';
  const byeWord = byes === 1 ? 'bye' : 'byes';
  const matchWord = playIns.length === 1 ? 'match' : 'matches';
  const nextRound = tree === 16 ? 'quarterfinals' : tree === 8 ? 'semifinals' : 'the final';
  const playLabel = playIns.map(([a, b]) => `${a} vs ${b}`).join(', ') || 'none';
  const customNote = n === 10
    ? 'not an 8-draw and not a custom 10-box tree'
    : `not a custom ${n}-box tree`;
  return {
    title: `${n} ${unit} use a ${tree}-draw with ${byes} ${byeWord}`,
    body: (
      `Knockout only works at 2, 4, 8, or 16 spots, so a ${n}-slot field is a ${tree}-draw — ${customNote}. `
      + `Round 1 is only ${playIns.length} play-in ${matchWord} (${playLabel}). `
      + `Everyone else starts in the ${nextRound} because they have no first-round opponent `
      + `(a bye is an empty first-round slot, not an extra win).`
    ),
  };
};

/** Alias used by singles (and doubles knockout) trees. */
export const singlesOpeningPairings = openingPairings;

export const isDoublesCategory = (categoryId) => normalizeCategoryId(categoryId).includes('doubles');
export const isSinglesCategory = (categoryId) => !isDoublesCategory(categoryId);
export const isMixedCategory = (categoryId) => normalizeCategoryId(categoryId).includes('mixed');

/** Under 50 vs 50+ / Seniors trees in one tournament (all club categories). */
export const BRACKET_DIVISION_OPEN = 'open';
export const BRACKET_DIVISION_50PLUS = '50plus';
export const MENS_DOUBLES_50PLUS_AGE = 50;

export const isAgeSplitCategory = (categoryId) => {
  const id = normalizeCategoryId(categoryId);
  return TOURNAMENT_CATEGORIES.some((c) => c.id === id);
};

/** Alias: same Under 50 / 50+ split for mixed, doubles, singles, and open singles. */
export const isMensDoublesAgeSplit = isAgeSplitCategory;

/** Wide age bands or any category that runs a separate 50+ tree. */
export const tournamentWelcomesSeniors = (tournament) =>
  isSeniorFriendlyAgeGroup(tournament?.age_group)
  || isAgeSplitCategory(tournament?.category);

export const seniorJoinHint = (tournament) => {
  if (isAgeSplitCategory(tournament?.category)) {
    return 'Seniors Open · 50+ play a separate bracket';
  }
  if (isSeniorFriendlyAgeGroup(tournament?.age_group)) {
    return 'Seniors Open';
  }
  return '';
};

/** Who-can-join line for cards/summaries (age band + 50+ split when present). */
export const ageGroupCaption = (tournament) => {
  const label = formatAgeGroupLabel(tournament?.age_group);
  if (isAgeSplitCategory(tournament?.category)) {
    return label
      ? `${label} · Under 50 and 50+ / Seniors brackets`
      : 'Under 50 and 50+ / Seniors brackets';
  }
  return label;
};

/** Logged-in player: whether their profile age fits this event's age group. */
export const playerAgeFitsHint = (user, tournament) => {
  const n = Number(user?.age);
  if (!Number.isFinite(n)) return '';
  const fits = ageFitsAgeGroup(n, tournament?.age_group);
  if (fits == null) return '';
  if (fits) {
    if (isAgeSplitCategory(tournament?.category) && n >= MENS_DOUBLES_50PLUS_AGE) {
      return `Your age (${n}) qualifies — you play in the 50+ / Seniors bracket`;
    }
    return `Your age (${n}) qualifies for this age group`;
  }
  const label = formatAgeGroupLabel(tournament?.age_group);
  return label
    ? `Your age (${n}) is outside ${label}`
    : `Your age (${n}) does not match this age group`;
};

export const publicBracketIsReady = (tournament) => {
  if (!tournament) return false;
  if (tournament.bracket_ready || tournament.bracket_ready_50plus) return true;
  const n = clampParticipantLimit(tournament.max_slots);
  if (usesRoundRobin(n)) return true;
  if (isMensDoublesAgeSplit(tournament.category)) {
    const n50 = clampParticipantLimit(tournament.max_slots_50plus ?? tournament.max_slots);
    if (usesRoundRobin(n50)) return true;
  }
  const named = (teams) => (Array.isArray(teams) ? teams : []).some(
    (t) => String(t?.players?.[0]?.name || '').trim(),
  );
  return named(tournament.teams) || named(tournament.teams_50plus);
};

/** Age-only cohort (ignores organizer play-up / play-down override). */
export const naturalBracketDivision = (participant) => {
  const age = Number(participant?.age);
  if (Number.isFinite(age) && age >= MENS_DOUBLES_50PLUS_AGE) {
    return BRACKET_DIVISION_50PLUS;
  }
  return BRACKET_DIVISION_OPEN;
};

/** Effective cohort: organizer override, else primary player age. */
export const participantBracketDivision = (participant) => {
  const override = String(participant?.bracket_division || '').trim().toLowerCase();
  if (override === BRACKET_DIVISION_OPEN || override === BRACKET_DIVISION_50PLUS) {
    return override;
  }
  return naturalBracketDivision(participant);
};

export const PARTICIPANT_DND_MIME = 'application/x-pickleball-participant';

export const defaultMensDoubles50PlusSlots = (tournament) =>
  clampParticipantLimit(tournament?.max_slots);

/**
 * Gender implied by the tournament division (Men's / Women's).
 * Unified Mixed Doubles: captain gender is free; partner is opposite (see partner helper).
 */
export const impliedGenderForCategory = (categoryId) => {
  const id = normalizeCategoryId(categoryId);
  if (isMixedCategory(id)) return null;
  // Check womens before mens — "womens_" contains the substring "mens_"
  if (id.includes('womens_')) return 'Female';
  if (id.includes('mens_')) return 'Male';
  return null;
};

/**
 * Partner gender for doubles.
 * Mixed Doubles: opposite of captainGender when provided; otherwise null until captain picks.
 * Men's/Women's Doubles: same gender as the division.
 */
export const impliedPartnerGenderForCategory = (categoryId, captainGender = null) => {
  const id = normalizeCategoryId(categoryId);
  if (!isDoublesCategory(id)) return null;
  if (isMixedCategory(id)) {
    if (captainGender === 'Male') return 'Female';
    if (captainGender === 'Female') return 'Male';
    return null;
  }
  return impliedGenderForCategory(id);
};

export const tournamentFormatLabel = (categoryId, storedLabel) => {
  if (storedLabel) return storedLabel;
  return isSinglesCategory(categoryId) ? 'Singles Elimination' : 'Bracket / Round Robin';
};

export const categoryFilterLabel = (categoryId) => {
  const normalized = normalizeCategoryId(categoryId);
  const cat = TOURNAMENT_CATEGORIES.find((c) => c.id === normalized);
  return cat?.filterLabel || cat?.label || normalized;
};

export const categoryDisplayLabel = (categoryId) => {
  const normalized = normalizeCategoryId(categoryId);
  const cat = TOURNAMENT_CATEGORIES.find((c) => c.id === normalized);
  return cat?.label || normalized;
};

export const GENDER_OPTIONS = ['Male', 'Female'];

export const PAYMENT_METHODS = [
  { id: 'gcash', label: 'GCash' },
  { id: 'maya', label: 'Maya' },
  { id: 'bank_transfer', label: 'Bank Transfer' },
  { id: 'cash', label: 'Cash' },
];

export const REGISTRATION_WIZARD_STEPS = [
  { id: 'tournament', label: 'Tournament' },
  { id: 'player', label: 'Player Information' },
  { id: 'payment', label: 'Payment' },
  { id: 'review', label: 'Review' },
  { id: 'complete', label: 'Complete' },
];

/** How long have you been playing Pickleball? */
export const PLAYING_EXPERIENCE_OPTIONS = [
  { id: 'less_than_6_months', label: 'Less than 6 months' },
  { id: '6_months_to_1_year', label: '6 months – 1 year' },
  { id: '1_to_2_years', label: '1–2 years' },
  { id: 'more_than_2_years', label: 'More than 2 years' },
];

/** Waiver & Agreement checkboxes (shared by public + admin registration). */
export const WAIVER_AGREEMENTS = [
  {
    key: 'waiver_certified',
    label: 'I certify that all information provided is true and correct.',
  },
  {
    key: 'waiver_age_verify',
    label: 'I understand that the age(s) submitted will be verified by tournament administrators.',
  },
  {
    key: 'waiver_rules',
    label: 'I agree to follow all tournament rules and decisions of the Tournament Committee.',
  },
  {
    key: 'waiver_liability',
    label:
      'I understand that participation is at my own risk and release the organizers from any liability for injuries or accidents.',
  },
];

export const WAIVER_FIELD_KEYS = WAIVER_AGREEMENTS.map((w) => w.key);

export const playingExperienceLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const byId = PLAYING_EXPERIENCE_OPTIONS.find((o) => o.id === raw);
  if (byId) return byId.label;
  const byLabel = PLAYING_EXPERIENCE_OPTIONS.find((o) => o.label === raw);
  if (byLabel) return byLabel.label;
  return raw;
};

export const defaultParticipantForm = (entryFee = 0) => ({
  full_name: '',
  nickname: '',
  date_of_birth: '',
  age: '',
  gender: '',
  city: '',
  contact_number: '',
  email: '',
  partner_full_name: '',
  partner_gender: '',
  partner_age: '',
  partner_contact_number: '',
  partner_email: '',
  emergency_contact_name: '',
  emergency_relationship: '',
  emergency_contact_number: '',
  playing_experience: '',
  previous_winner: false,
  previous_winner_details: '',
  previous_winner_proof_url: '',
  previous_winner_proof_filename: '',
  previous_winner_proof_file_id: '',
  dupr_rating: '',
  dupr_profile_url: '',
  player_skill_level: '',
  played_up: false,
  tshirt_size: '',
  partner_tshirt_size: '',
  waiver_certified: false,
  waiver_age_verify: false,
  waiver_rules: false,
  waiver_liability: false,
  registration_fee: entryFee,
  additional_fees: 0,
  payment_method: '',
  payment_reference: '',
  payment_proof_url: '',
  payment_verified: false,
  age_verified: false,
  notes: '',
});

/** Normalize PH mobile to 09XXXXXXXXX digits for registration inputs. */
export const normalizePhMobileDigits = (value) => {
  let cleaned = String(value || '').replace(/\D/g, '');
  if (cleaned.startsWith('63') && cleaned.length >= 12) {
    cleaned = `0${cleaned.slice(2)}`;
  }
  if (!cleaned.startsWith('0') && cleaned.length === 10) {
    cleaned = `0${cleaned}`;
  }
  return cleaned.slice(0, 11);
};

/** Prefill wizard fields from a logged-in user profile when available. */
export const prefillFromUser = (form, user) => {
  if (!user) return form;
  const ageValue =
    form.age !== '' && form.age != null
      ? form.age
      : (user.age != null && user.age !== '' ? String(user.age) : '');
  const phoneFromUser = normalizePhMobileDigits(user.phone);
  const phoneOk = /^09\d{9}$/.test(phoneFromUser);
  return {
    ...form,
    full_name: form.full_name || user.name || '',
    email: form.email || user.email || '',
    contact_number: form.contact_number || (phoneOk ? phoneFromUser : ''),
    gender: form.gender || user.gender || '',
    age: ageValue,
    player_skill_level: form.player_skill_level || normalizeSkillLevel(user.skill_level),
  };
};

/** localStorage key — first-time End Tournament walkthrough */
export const END_TOURNAMENT_TOUR_KEY = 'pickleball_end_tournament_tour_seen_v1';

export const DEFAULT_TOURNAMENT_VENUE = 'CamSur Pickleball Club';
export const DEFAULT_TOURNAMENT_VENUE_ADDRESS =
  'CamSur Pickleball Club, Capitol Complex, Cadlan, Pili, Camarines Sur.';

/** Official DUPR signup — for players who do not have a rating yet. */
export const DUPR_SIGNUP_URL = 'https://dashboard.dupr.com/signup';

/**
 * Extract and normalize a DUPR profile URL from raw paste
 * (plain URL, player id, or a message that contains the link).
 * Accepts: https://dashboard.dupr.com/dashboard/player/4965412923
 * and the same path with optional /profile suffix.
 */
export const normalizeDuprProfileUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  // Prefer first http(s) URL in pasted text
  const httpMatch = raw.match(/https?:\/\/[^\s<>"']+/i);
  // Or a bare dashboard.dupr.com path
  const bareMatch = raw.match(/(?:www\.)?dashboard\.dupr\.com\/[^\s<>"']+/i);
  // Or a numeric DUPR player id alone
  const idOnly = raw.match(/^\s*(\d{6,})\s*$/);

  let candidate = '';
  if (httpMatch) {
    candidate = httpMatch[0];
  } else if (bareMatch) {
    candidate = `https://${bareMatch[0].replace(/^www\./i, '')}`;
  } else if (idOnly) {
    candidate = `https://dashboard.dupr.com/dashboard/player/${idOnly[1]}`;
  } else if (/^https?:\/\//i.test(raw)) {
    candidate = raw;
  } else if (/dashboard\.dupr\.com/i.test(raw) || raw.includes('/')) {
    candidate = `https://${raw.replace(/^\/+/, '')}`;
  } else {
    return '';
  }

  // Strip trailing punctuation from chat paste ("…profile.")
  candidate = candidate.replace(/[.,);\]>'"`]+$/g, '');

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return '';
    // Keep player profile paths usable even without /profile
    const playerMatch = parsed.pathname.match(/\/dashboard\/player\/(\d+)(?:\/profile)?\/?/i);
    if (playerMatch) {
      return `https://dashboard.dupr.com/dashboard/player/${playerMatch[1]}`;
    }
    return parsed.toString().replace(/\/$/, '');
  } catch {
    return '';
  }
};

/** True when paste resolves to an openable DUPR (or generic http) profile URL. */
export const isDuprProfileUrl = (value) => {
  const url = normalizeDuprProfileUrl(value);
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/** Tournament kit / registration t-shirt sizes. */
export const TSHIRT_SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

export const defaultTournamentForm = () => ({
  title: '',
  category: 'mens_singles',
  skill_level: 'Novice',
  age_group: '18-25',
  tournament_date: '',
  start_time: '08:30',
  end_time: '17:00',
  entry_fee: 1200,
  /** Doubles/mixed only: when true, charge entry_fee × 2 (per player). Default false = amount as entered. */
  entry_fee_per_player: false,
  max_slots: 16,
  registration_deadline: '',
  description: '',
  venue_name: DEFAULT_TOURNAMENT_VENUE,
});

/**
 * Amount charged for one registration based on tournament fee settings.
 * Singles always use entry_fee. Doubles only double when entry_fee_per_player is true.
 */
export const resolveTournamentEntryFee = (tournament) => {
  const n = Number(tournament?.entry_fee);
  if (!Number.isFinite(n) || n <= 0) return 0;
  const doubles = isDoublesCategory(tournament?.category);
  if (doubles && tournament?.entry_fee_per_player) return Math.round(n * 2 * 100) / 100;
  return Math.round(n * 100) / 100;
};

export const formatTournamentEntryFee = (tournament, { withSuffix = true } = {}) => {
  const amount = resolveTournamentEntryFee(tournament);
  const formatted = `₱${amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (!withSuffix) return formatted;
  const doubles = isDoublesCategory(tournament?.category);
  if (!doubles) return formatted;
  return tournament?.entry_fee_per_player
    ? `${formatted} (per player × 2)`
    : `${formatted} (per pair)`;
};

/** Landing banner panel colors + fallback hero photos per category. */
export const CATEGORY_BANNER_THEMES = {
  mens_singles: {
    panel: '#5BA4D9',
    accent: '#194e7f',
    fallbackImage: '/newassets/b1.jpg',
  },
  mens_doubles: {
    panel: '#4EC4D9',
    accent: '#0e7490',
    fallbackImage: '/newassets/b2.jpg',
  },
  womens_singles: {
    panel: '#E84A8A',
    accent: '#be185d',
    fallbackImage: '/newassets/b4.jpg',
  },
  womens_doubles: {
    panel: '#B191FF',
    accent: '#7c3aed',
    fallbackImage: '/newassets/b5.jpg',
  },
  mixed_doubles: {
    panel: '#2BB673',
    accent: '#15803d',
    fallbackImage: '/newassets/b2.jpg',
  },
  open_mens_singles: {
    panel: '#1e4b85',
    accent: '#0f2747',
    fallbackImage: '/newassets/b5.jpg',
  },
  open_womens_singles: {
    panel: '#7c3aed',
    accent: '#4c1d95',
    fallbackImage: '/newassets/b5.jpg',
  },
};

/** Resolve banner theme; maps legacy mixed IDs to unified Mixed Doubles. */
export const getCategoryBannerTheme = (categoryId) => {
  const normalized = normalizeCategoryId(categoryId);
  return CATEGORY_BANNER_THEMES[normalized] || CATEGORY_BANNER_THEMES.mens_singles;
};

export const TOURNAMENT_LOGO = '/newassets/logo%201.png';

/** Format YYYY-MM-DD → DD/MM/YYYY (e.g. 2026-07-12 → 12/07/2026). */
export const formatTournamentDate = (isoDate) => {
  if (!isoDate) return '—';
  const [y, m, d] = String(isoDate).split('-');
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
};

/** Format YYYY-MM-DD → long US style (e.g. 2026-08-03 → August 3, 2026). */
export const formatTournamentDateLong = (isoDate) => {
  if (!isoDate) return '—';
  try {
    const raw = String(isoDate).trim();
    const dayPart = raw.includes('T') ? raw.slice(0, 10) : raw.slice(0, 10);
    const d = new Date(`${dayPart}T12:00:00`);
    if (Number.isNaN(d.getTime())) return formatTournamentDate(isoDate);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return formatTournamentDate(isoDate);
  }
};
